/*
**Where terrain tiles go.** The pure arithmetic under [[b3d-terrain]]'s
streamed level-of-detail tiles: where a tile sits in the world, which world
point each of its vertices samples, how far a level reaches, and which coarse
tiles a finer level hides. No Babylon and no DOM, so "are the tiles placed and
sampled correctly" is a unit test and not something you squint at.

```javascript
import { lodTileSize, tileCenter, cellIndex } from 'tosijs-3d/terrain-grid'

lodTileSize(128, 2) // 512: each level doubles the tile
cellIndex(700, 512) // 1: which tile a coordinate falls in
tileCenter(1, -2, 512) // { x: 512, z: -1024 }
```

You only need this if you are building on the tile substrate yourself (a
volumetric patch, a custom streamer). `desiredCells` is the quadtree: given a
viewer, the set of tiles that should exist. See also [[sdf-lattice]].
*/

/**
 * Pure terrain grid math — no Babylon, no DOM, so it unit-tests headless. This is
 * the coordinate system the streamed LOD tiles live in: where a tile sits in the
 * world (placement), which world point each of its vertices samples (sampling),
 * how big a level's coverage square is, and which coarse tiles a finer level hides
 * (culling). Keeping it pure means "are the tiles sampling/placed correctly?" is a
 * test, not a wireframe squint. See terrain-grid.test.ts.
 *
 * Conventions: a tile at integer grid cell (gx, gz) is CENTRED on the world point
 * (gx·tileSize, gz·tileSize) and spans ±tileSize/2. Level k uses tiles of
 * `baseTileSize · 2^k · horizScale`; because a cell's world centre is `gx·tileSize`
 * and tileSize doubles per level, a coarse tile centre is an even multiple of the
 * finer tile size — coarse and fine grids stay aligned, and a coarse vertex always
 * lands on a finer vertex.
 */

/** Edge length of a level's tile: base · 2^level · horizScale. */
export function lodTileSize(
  baseTileSize: number,
  level: number,
  horizScale = 1
): number {
  return baseTileSize * Math.pow(2, level) * horizScale
}

/** World centre of the tile at grid cell (gx, gz) — where its mesh is placed. */
export function tileCenter(
  gx: number,
  gz: number,
  tileSize: number
): { x: number; z: number } {
  return { x: gx * tileSize, z: gz * tileSize }
}

/**
 * Local offset of vertex `i` (0…subdivisions) from the tile centre along an axis,
 * spanning [-tileSize/2, +tileSize/2]. `flip` negates it (the Z axis is built
 * top-down in the mesh, so its rows run +half → −half).
 */
export function vertexLocal(
  i: number,
  subdivisions: number,
  tileSize: number,
  flip = false
): number {
  const t = (i / subdivisions - 0.5) * tileSize
  return flip ? -t : t
}

/** World coordinate vertex `i` of tile `g` samples on one axis: centre + local. */
export function vertexWorld(
  g: number,
  i: number,
  subdivisions: number,
  tileSize: number,
  flip = false
): number {
  return g * tileSize + vertexLocal(i, subdivisions, tileSize, flip)
}

/** Grid cell a world coordinate falls in, for a given tile size. */
export function cellIndex(coord: number, tileSize: number): number {
  return Math.round(coord / tileSize)
}

/**
 * Half-extent of a level's coverage square: an `hiResGrid`-wide block of tiles
 * around the camera reaches `(floor(hiResGrid/2) + 0.5)·tileSize` from centre.
 */
export function coverageHalf(hiResGrid: number, tileSize: number): number {
  return (Math.floor(hiResGrid / 2) + 0.5) * tileSize
}

/** Is the span [c−half, c+half] fully within [cc−ch, cc+ch]? (one axis) */
export function spanInside(
  c: number,
  half: number,
  cc: number,
  ch: number
): boolean {
  return c - half >= cc - ch && c + half <= cc + ch
}

// ─── Priority pool: corner-indexed quadtree LOD ────────────────────────────
// A cell at (gx, gz, level) spans world [gx·s, (gx+1)·s] where s = base·2^level;
// its 4 children at level-1 are (2gx+{0,1}, 2gz+{0,1}), so a coarse cell IS
// exactly four finer cells — no overlap or gap possible, unlike the ring scheme.

export interface DesiredCell {
  gx: number
  gz: number
  level: number
  tileSize: number
  /** Cell centre (where its tile mesh is placed). */
  cx: number
  cz: number
  /** Higher = more wanted (fill first / steal last). */
  priority: number
}

export interface QuadtreeConfig {
  baseTileSize: number
  /** Total LOD levels; the coarsest is `levels - 1`. */
  levels: number
  /** Subdivide a cell when the camera is nearer than `splitFactor · tileSize`. */
  splitFactor: number
  /** Terrain reaches this far from the camera (cells fully beyond are dropped). */
  maxReach: number
  /** Inside this radius, priority ignores direction (omni — safe to look around). */
  omniRadius: number
  /** Unit facing/travel direction; beyond omniRadius, cells ahead outrank those
   * behind. Omit for undirected (everything omni). */
  interest?: { x: number; z: number }
  /**
   * Regions that must be resolved FINER than distance alone would choose —
   * volumetric patches (a bore mouth needs fine tiles to cut a clean hole in).
   * Cost is bounded by the region's AREA, not its distance: a 60 m footprint is
   * a handful of fine tiles wherever it sits, so this can't blow up the tile
   * count the way a distance-based override would.
   */
  refine?: RefineRegion[]
}

/** A world-XZ box that must be tiled at `level` or finer (0 = finest). */
export interface RefineRegion {
  minX: number
  maxX: number
  minZ: number
  maxZ: number
  level: number
}

/**
 * The set of terrain cells that SHOULD exist right now, as a quadtree around the
 * camera: fine near, coarse far, exactly one LOD per patch of ground (no overlap,
 * no gap). Each carries a `priority` so a fixed pool can fill/steal by importance.
 * Pure — the allocator just diffs this against what's currently placed.
 *
 * Allocates a fresh array; for the per-frame streaming hot path use
 * `desiredCellsInto`, which reuses a caller-owned array (and its cell objects) to
 * avoid the GC churn of rebuilding ~70 objects every frame.
 */
export function desiredCells(
  camX: number,
  camZ: number,
  cfg: QuadtreeConfig
): DesiredCell[] {
  return desiredCellsInto(camX, camZ, cfg, [])
}

/**
 * As `desiredCells`, but fills `out` in place — reusing its existing cell objects
 * (mutated) and truncating any surplus, so a steady camera reuses the same ~70
 * objects frame after frame instead of allocating a new array of new objects each
 * time. Returns `out`. This is the pure, poolable core; `desiredCells` wraps it.
 */
export function desiredCellsInto(
  camX: number,
  camZ: number,
  cfg: QuadtreeConfig,
  out: DesiredCell[]
): DesiredCell[] {
  const top = Math.max(0, cfg.levels - 1)
  const rootSize = cfg.baseTileSize * Math.pow(2, top)
  const reach = cfg.maxReach
  const dir = cfg.interest
  let n = 0

  const emit = (gx: number, gz: number, level: number) => {
    const ts = cfg.baseTileSize * Math.pow(2, level)
    const cx = (gx + 0.5) * ts
    const cz = (gz + 0.5) * ts
    const dx = cx - camX
    const dz = cz - camZ
    const dist = Math.hypot(dx, dz)
    let priority = 1 / (1 + dist) // near = high
    if (dist >= cfg.omniRadius && dir) {
      const align = (dx * dir.x + dz * dir.z) / (dist || 1) // −1 behind … +1 ahead
      priority *= 0.1 + 0.9 * ((align + 1) / 2) // behind ~0.1, side ~0.55, ahead 1
    }
    // Reuse the slot's object if there is one; only grow the array when needed.
    const cell = out[n]
    if (cell == null) {
      out[n] = { gx, gz, level, tileSize: ts, cx, cz, priority }
    } else {
      cell.gx = gx
      cell.gz = gz
      cell.level = level
      cell.tileSize = ts
      cell.cx = cx
      cell.cz = cz
      cell.priority = priority
    }
    n++
  }

  const descend = (gx: number, gz: number, level: number) => {
    const ts = cfg.baseTileSize * Math.pow(2, level)
    const cx = (gx + 0.5) * ts
    const cz = (gz + 0.5) * ts
    // Drop cells whose nearest corner is beyond the reach disk.
    const ndx = Math.max(0, Math.abs(cx - camX) - ts / 2)
    const ndz = Math.max(0, Math.abs(cz - camZ) - ts / 2)
    if (Math.hypot(ndx, ndz) > reach) return
    const dist = Math.hypot(cx - camX, cz - camZ)
    // A refine region forces descent even where distance says "coarse is fine".
    let forced = false
    if (cfg.refine != null && level > 0) {
      const half = ts / 2
      for (const r of cfg.refine) {
        if (
          level > r.level &&
          cx + half > r.minX &&
          cx - half < r.maxX &&
          cz + half > r.minZ &&
          cz - half < r.maxZ
        ) {
          forced = true
          break
        }
      }
    }
    if (level > 0 && (forced || dist < cfg.splitFactor * ts)) {
      descend(2 * gx, 2 * gz, level - 1)
      descend(2 * gx + 1, 2 * gz, level - 1)
      descend(2 * gx, 2 * gz + 1, level - 1)
      descend(2 * gx + 1, 2 * gz + 1, level - 1)
    } else {
      emit(gx, gz, level)
    }
  }

  const g0 = Math.floor((camX - reach) / rootSize)
  const g1 = Math.floor((camX + reach) / rootSize)
  const h0 = Math.floor((camZ - reach) / rootSize)
  const h1 = Math.floor((camZ + reach) / rootSize)
  for (let gx = g0; gx <= g1; gx++) {
    for (let gz = h0; gz <= h1; gz++) descend(gx, gz, top)
  }
  out.length = n // drop any surplus objects from a busier previous frame
  return out
}

/**
 * Scratch size for `buildTileField`: a padded (subdivisions + 3)² height grid — the
 * tile's own vertices plus **one ring beyond each edge**, which is what the normals
 * need. Allocate once per subdivision count and reuse it for every tile; the streamer
 * builds tiles every frame, so this must not allocate.
 */
/** Noise samples per tile build — the padded grid, sampled once each.
 * TWO rings beyond each edge: ring 1 for the normal differences, ring 2 so
 * the low-passed normalSmoothing field is itself defined at ring 1.
 * (+5 = subdivisions+1 verts + 2 rings per side.) */
export const tileFieldSampleCount = (subdivisions: number): number =>
  (subdivisions + 5) * (subdivisions + 5)

/** Scratch floats per tile build: the sampled grid PLUS the low-passed copy
 * the normals difference when smoothing is on. */
export const tileFieldScratchSize = (subdivisions: number): number =>
  tileFieldSampleCount(subdivisions) * 2

/**
 * Build one tile's heightfield + normals into caller-owned buffers. **Pure**: it knows
 * nothing about Babylon, meshes, or the noise model — you pass `heightAt`, it fills
 * `positions` and `normals`.
 *
 * ## Why it samples a padded grid
 *
 * The normal at a vertex is the height-field gradient, central-differenced over ±e where
 * `e = tileSize / subdivisions`. But that spacing IS the vertex spacing — so `heightAt(wx
 * ± e, wz)` is *precisely the height of the neighbouring vertex*. Sampling ±e per vertex
 * therefore recomputes, five times over, heights the tile is about to compute anyway.
 *
 * So: sample a grid ONE RING wider than the tile, then difference neighbours. Same values
 * (identical heights; normals agree to float32 rounding), and the noise evaluations drop
 * from `(subs+1)² × 5` to `(subs+3)²` — ~4.3× fewer at subs 24, measured ~3.7–4.8× faster.
 * The normals stay *analytic* (a function of world position, not of mesh topology), so
 * same-level neighbouring tiles still agree exactly on a shared edge vertex — which is
 * what keeps the lighting seam away.
 *
 * ## Why it looks like this
 *
 * Fixed, caller-owned buffers; no allocation; one call does a whole tile. That's the shape
 * a wasm kernel wants (see PERF-DESIGN.md) — if tile building ever moves to a worker or to
 * wasm, THIS is the function that gets replaced, and the differential test that pins it
 * (terrain-grid.test.ts) becomes the conformance test for the port.
 */
export function buildTileField(
  heightAt: (wx: number, wz: number) => number,
  cx: number,
  cz: number,
  subdivisions: number,
  tileSize: number,
  scratch: Float64Array,
  // Babylon's getVerticesData hands back a FloatArray (Float32Array | number[]); this
  // module stays Babylon-free, so accept both rather than lie with a cast.
  positions: Float32Array | number[],
  normals: Float32Array | number[],
  /**
   * 0..1 — how much the NORMALS see a low-passed (tent-filtered) height field.
   * Positions always keep the crisp heights, so the silhouette is untouched;
   * only the SHADING softens. This kills the cliff-face zigzag: a profile
   * riser narrower than a grid cell makes adjacent vertices' central
   * differences alternate between seeing and missing the wall — sawtooth
   * banding down the face. 0 = classic sharp normals.
   */
  normalSmoothing = 0
): void {
  const verts = subdivisions + 1
  const P = verts + 4 // padded side: TWO rings beyond each edge
  const N = P * P
  const e = tileSize / subdivisions // finite-difference step === vertex spacing

  // 1. Heights over the padded grid, indices -2 … subdivisions+2 on both axes.
  for (let jz = 0; jz < P; jz++) {
    const wz = cz + vertexLocal(jz - 2, subdivisions, tileSize, true)
    for (let jx = 0; jx < P; jx++) {
      const wx = cx + vertexLocal(jx - 2, subdivisions, tileSize)
      scratch[jz * P + jx] = heightAt(wx, wz)
    }
  }

  // 1b. The normals' height field: crisp, or blended toward a 5-tap tent.
  // Written into the scratch's second half — same padded indexing, defined on
  // ring 1 (its own neighbours live on ring 2), no allocation.
  const k = normalSmoothing < 0 ? 0 : normalSmoothing > 1 ? 1 : normalSmoothing
  if (k > 0) {
    for (let jz = 1; jz < P - 1; jz++) {
      for (let jx = 1; jx < P - 1; jx++) {
        const j = jz * P + jx
        const tent =
          (4 * scratch[j] +
            scratch[j - 1] +
            scratch[j + 1] +
            scratch[j - P] +
            scratch[j + P]) /
          8
        scratch[N + j] = scratch[j] + (tent - scratch[j]) * k
      }
    }
  }
  const H = k > 0 ? N : 0 // offset of the field the normals difference

  // 2. Positions + analytic normals, differencing the neighbours we just sampled.
  const ny = 2 * e
  for (let iz = 0; iz < verts; iz++) {
    const localZ = vertexLocal(iz, subdivisions, tileSize, true)
    for (let ix = 0; ix < verts; ix++) {
      const j = (iz + 2) * P + (ix + 2)
      const nx = scratch[H + j - 1] - scratch[H + j + 1] // h(wx-e) − h(wx+e)
      // localZ is FLIPPED, so the iz+1 neighbour lies at wz − e (not wz + e): the row
      // BELOW in the scratch grid is the one at wz − e. Get this backwards and every
      // normal's z is mirrored — the terrain lights from the wrong side.
      const nz = scratch[H + j + P] - scratch[H + j - P] // h(wz-e) − h(wz+e)
      const inv = 1 / Math.hypot(nx, ny, nz)

      const v = iz * verts + ix
      positions[v * 3] = vertexLocal(ix, subdivisions, tileSize)
      positions[v * 3 + 1] = scratch[j] // CRISP height — silhouette untouched
      positions[v * 3 + 2] = localZ
      normals[v * 3] = nx * inv
      normals[v * 3 + 1] = ny * inv
      normals[v * 3 + 2] = nz * inv
    }
  }
}

/**
 * The index list a tile should DRAW, given which of its grid vertices a
 * volumetric patch has masked away — or `null` for "use the shared template",
 * which is the answer for the overwhelming majority of tiles.
 *
 * Returning `null` rather than a copy of the template is the point: it's what
 * lets the caller tell "this tile needs its own buffer" from "this tile is
 * ordinary", and therefore what makes the RELEASE path expressible — a pooled
 * tile that once had a hole must go back to the template when it's reused
 * somewhere else, or it renders a hole in ground that has none.
 *
 * A quad is dropped when ANY of its four corners is masked, so the hole comes
 * out slightly LARGER than the mask. That's deliberate: the patch's rim is
 * tucked just below the terrain surface (see `patch-field.marginBlend`), so an
 * over-large hole is hidden by the bore's own geometry, while an under-large
 * one would leave tile triangles poking through the tunnel wall.
 *
 * The skirt ring is kept EXCEPT where the hole eats it. A skirt hangs straight
 * down from the tile's perimeter to hide the LOD seam — which is invisible
 * while it's buried in solid ground, and a curtain hanging in mid-air the
 * moment the ground beneath it has been carved away. When a bore lands on a
 * tile boundary (four tiles meeting at a shaft is the normal case, not a
 * pathological one — tile corners are on a grid and so are authored features),
 * those tiles' skirts drop straight through the open bore. So a skirt quad
 * goes when either of its perimeter vertices is masked, and the rest of the
 * ring stays.
 */
export function tileIndexPlan(
  tpl: {
    gridCount: number
    perim: number[]
    gridIndices: number[]
    allIndices: number[]
  },
  isMasked: ((vertex: number) => boolean) | null,
  /** Filled with 1 for each RIM vertex — masked, but touching unmasked ground.
   * The caller drops those vertices to make a collar folding into the hole
   * (see `tileIndexPlan`'s note on the rim). */
  rimOut?: Uint8Array
): number[] | null {
  if (isMasked == null) return null
  const masked = new Uint8Array(tpl.gridCount)
  let any = false
  for (let v = 0; v < tpl.gridCount; v++) {
    if (isMasked(v)) {
      masked[v] = 1
      any = true
    }
  }
  if (!any) return null // touched by a patch's bounds, but nothing to cut

  /*
  THE RIM COLLAR. A hole cut cleanly at the grid leaves the terrain's edge
  hanging in space, and the tunnel below starts wherever its own surface is —
  so you see daylight between them (Tonio: "visible gap between tunnel start
  and hole in ground", with the fix: skirt down from the hole edge, and flange
  the tunnel out to meet it).

  A masked vertex TOUCHING unmasked ground keeps its quads instead of losing
  them; the caller then drops that vertex, so the terrain folds down into the
  hole as a collar rather than stopping at a cliff edge. Only the first ring
  survives — everything deeper is genuinely gone.
  */
  const rim = rimOut ?? new Uint8Array(tpl.gridCount)
  rim.fill(0)
  const g0 = tpl.gridIndices
  for (let i = 0; i < g0.length; i += 6) {
    const corners = [g0[i], g0[i + 1], g0[i + 2], g0[i + 5]]
    let m = 0
    for (const c of corners) if (masked[c]) m++
    if (m === 0 || m === 4) continue // wholly outside or wholly inside the hole
    for (const c of corners) if (masked[c]) rim[c] = 1
  }
  // A rim vertex is dropped, not removed: its quads stay so the collar exists.
  for (let v = 0; v < tpl.gridCount; v++) if (rim[v]) masked[v] = 0
  const out: number[] = []
  const g = tpl.gridIndices
  for (let i = 0; i < g.length; i += 6) {
    // one quad = two triangles over corners {a, b, c, d} = g[i], g[i+2], g[i+1], g[i+5]
    if (
      masked[g[i]] ||
      masked[g[i + 1]] ||
      masked[g[i + 2]] ||
      masked[g[i + 5]]
    ) {
      continue
    }
    for (let k = 0; k < 6; k++) out.push(g[i + k])
  }
  // …then the skirt, minus any quad whose ground has been carved out from
  // under it. Rebuilt from `perim` rather than sliced out of `allIndices`, so
  // the two can't drift apart if the template's layout ever changes.
  const pc = tpl.perim.length
  for (let p = 0; p < pc; p++) {
    const pn = (p + 1) % pc
    const ga = tpl.perim[p]
    const gb = tpl.perim[pn]
    if (masked[ga] || masked[gb]) continue
    out.push(
      ga,
      tpl.gridCount + p,
      gb,
      gb,
      tpl.gridCount + p,
      tpl.gridCount + pn
    )
  }
  return out
}

/**
 * The LOD level the quadtree would naturally use at `dist` from the camera —
 * i.e. how coarse the ground over there is about to be.
 *
 * This is what decides whether a volumetric patch is worth resolving at all:
 * if the terrain around a bore is coarser than the patch's own detail level,
 * SEAL it — mask nothing, extract no walls, let the ground close over the
 * tunnel. Beyond that distance the hole is sub-pixel, and its wall chunks
 * would be paying full price to be invisible. (It also dodges an aliasing
 * mismatch the plan flags: coarse tiles band-limit their noise, an SDF never
 * does.)
 */
export function naturalLevel(dist: number, cfg: QuadtreeConfig): number {
  const top = Math.max(0, cfg.levels - 1)
  for (let level = 0; level < top; level++) {
    const ts = cfg.baseTileSize * Math.pow(2, level)
    if (dist < cfg.splitFactor * ts) return level
  }
  return top
}

/**
 * Is a patch worth resolving from here? True when the surrounding terrain is
 * at least as fine as the patch's `detailLevel`. Ref-count patch residency off
 * this — don't let a patch own tiles, or a bore outlives the ground it's cut
 * into and you get a tunnel floating in the air.
 */
export function patchResident(
  patchCenterX: number,
  patchCenterZ: number,
  detailLevel: number,
  camX: number,
  camZ: number,
  cfg: QuadtreeConfig
): boolean {
  const dist = Math.hypot(patchCenterX - camX, patchCenterZ - camZ)
  if (dist > cfg.maxReach) return false
  return naturalLevel(dist, cfg) <= detailLevel
}

/**
 * Widest the finest LOD may get, in tiles across.
 *
 * 256² = 65,536 tiles is already generous — the reported tab-killer was a
 * million. Not an attribute: it is a survival limit, and a knob for it would
 * just be the same footgun with an extra step.
 */
export const MAX_TILES_ACROSS = 256

/**
 * Clamp a requested `reach` against `tileSize`, so the product cannot kill a tab.
 *
 * Finest-level tiles go as `(2·reach / tileSize)²` and the two are separate
 * controls, so it is the PRODUCT that bites: reach 5000 at tileSize 10 is a
 * million tiles (tosijs-3d-ensemble put a slider on it and lost the tab, #66).
 * They capped `reach` at 400 m as a guess and named the real problem — a JSON
 * Schema cannot say "…unless tileSize is small". The element knows both
 * numbers, so the clamp belongs with them.
 *
 * A clamp rather than a refusal: a terrain drawing a smaller world is
 * recoverable and visibly odd, where one that refuses to draw looks broken and
 * reads as a different bug entirely.
 *
 * Lives here rather than in the element so it can be asserted without a scene —
 * the test used to restate the arithmetic, which meant it pinned a copy.
 */
export function budgetedReach(
  asked: number,
  tileSize: number
): { reach: number; across: number; clamped: boolean } {
  const tile = tileSize > 0 ? tileSize : 1
  // Across, not area: the limit people reason about is "how many tiles wide".
  const across = (2 * asked) / tile
  if (across <= MAX_TILES_ACROSS)
    return { reach: asked, across, clamped: false }
  return { reach: (MAX_TILES_ACROSS * tile) / 2, across, clamped: true }
}
